import {
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { pickGatorProtectSiteWithoutProtectionData } from '@/redux/modules';
import { MONETIZACAO_ENABLE_THREATS_DETECTED_MODAL } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import type { GPModalsVariants } from '../gatorProtectModalsVisibilityReducer';
import type { HideGatorProtectModalSlot } from '../useGatorProtectModalsVisibility';
import { GP_MODAL_VARIANT_DETECTED_THREADS } from './GatorProtectDetectedThreadsModal.types';
import type {
  DetectedThreadsPayload,
  GatorProtectGlobalsDependencies,
  GatorProtectPortalModalsRootState,
} from '../GatorProtectGlobals.types';
import type { GatorProtectModalHookReturn } from '../gatorProtectModalHooks.types';
import { FALLBACK_PORTAL_MODALS } from '../gatorProtectPortalModals.shared';

export type UseGatorProtectDetectedThreadsModalParams = {
  summary: GatorProtectGlobalsDependencies['summary'];
  isHome: boolean;
  hideModalSlot: HideGatorProtectModalSlot;
};

export type UseGatorProtectDetectedThreadsModalData = {
  detectedThreads: DetectedThreadsPayload | null;
};

export type UseGatorProtectDetectedThreadsModalUtils = {
  hideDetectedThreadsModal: (_topStackModalVariant: GPModalsVariants | null) => void;
};

export type UseGatorProtectDetectedThreadsModalReturn = GatorProtectModalHookReturn<
  UseGatorProtectDetectedThreadsModalData,
  UseGatorProtectDetectedThreadsModalUtils
>;

/**
 * Detected-threads modal from portal modals (`site-without-protection`) + GrowthBook gate + home/feature flags.
 * Portal modals fetch / reset lives in `useGatorProtectGlobal`.
 * Dismiss-slot visibility is composed in `GatorProtectGlobals`.
 */
export function useGatorProtectDetectedThreadsModal({
  summary,
  isHome,
  hideModalSlot,
}: UseGatorProtectDetectedThreadsModalParams): UseGatorProtectDetectedThreadsModalReturn {
  const gatorProtectEnabled = summary.features?.gator_protect ?? false;
  const threatModalFeatureOn = useFeatureIsOn(MONETIZACAO_ENABLE_THREATS_DETECTED_MODAL);

  const portalModalsState = useSelector((state: GatorProtectPortalModalsRootState) => state.portalModals);
  const apiModals = portalModalsState ?? FALLBACK_PORTAL_MODALS;

  const detectedThreadsData = useMemo((): DetectedThreadsPayload | null => {
    const data = pickGatorProtectSiteWithoutProtectionData({
      modals: apiModals.modals,
    });
    const hostingId = data?.hosting_id;
    if (data == null || hostingId == null || Number.isNaN(Number(hostingId))) {
      return null;
    }
    return {
      threatCount: Math.max(0, Number(data.threats) || 0),
      hostingId: Number(hostingId),
    };
  }, [apiModals.modals]);

  const meetsDetectedThreadsRules = Boolean(isHome
    && gatorProtectEnabled
    && threatModalFeatureOn
    && detectedThreadsData !== null);

  const hideDetectedThreadsModal = useCallback((topStackModalVariant: GPModalsVariants | null) => {
    if (topStackModalVariant !== GP_MODAL_VARIANT_DETECTED_THREADS) {
      return;
    }
    hideModalSlot(GP_MODAL_VARIANT_DETECTED_THREADS);
  }, [hideModalSlot]);

  return {
    data: { detectedThreads: detectedThreadsData },
    utils: {
      hideDetectedThreadsModal,
    },
    visibilityRule: meetsDetectedThreadsRules,
  };
}
